import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Axios, { AxiosResponse } from 'axios';
import { RootState } from '..';

export interface IdleGameRecommendationReducer {
  isOpen: boolean;
  recommendedGame: Array<any>;
  isLoading: boolean;
}

const apiUrl: string = window.config.betsApiUrl + '/api/search/v1/player/recommended-games/';
const IGR_KEY: string = 'idleGameRecommendation';
const TTL_PARAM = parseInt(window?.config?.idleGameRecomendationExpirationSeconds) * 1000;
const getExpireTimestamp = () => Math.floor(Date.now() / 1000) + TTL_PARAM / 1000;

const setIGRData = (data: any) => {
  localStorage.setItem(IGR_KEY, JSON.stringify(data));
};

const getIGRData = () => {
  const raw = localStorage.getItem(IGR_KEY);
  return raw ? JSON.parse(raw) : null;
};

let prefetchedGameCache: any = null;

export const openIGRPopupAsync = createAsyncThunk<
  { recommendedGame: any; isOpen: boolean; isLoading: boolean },
  { prefetch?: boolean } | void,
  { state: RootState }
>('idleGameRecommendation/openIGRPopup', async (arg, { getState, rejectWithValue }) => {
  const prefetch = typeof arg === 'object' && arg?.prefetch === true;

  if (!prefetch && prefetchedGameCache) {
    const igrData = getIGRData();
    const expireTimestamp = getExpireTimestamp();

    if (igrData && !igrData.recommended_games.some((g: any) => g.game_id === prefetchedGameCache.id.toString())) {
      igrData.recommended_games.push({
        game_id: prefetchedGameCache.id.toString(),
        expire_timestamp: expireTimestamp,
      });
      setIGRData(igrData);
    }

    const popupGame = prefetchedGameCache;
    prefetchedGameCache = null;

    return { recommendedGame: popupGame, isOpen: true, isLoading: false };
  }

  const expireTimestamp = getExpireTimestamp();
  let igrData = getIGRData();
  const now = Math.floor(Date.now() / 1000);

  if (igrData) {
    igrData.played_games = igrData.played_games.filter((g: any) => !g.expire_timestamp || g.expire_timestamp > now);
    igrData.recommended_games = igrData.recommended_games.filter(
      (g: any) => !g.expire_timestamp || g.expire_timestamp > now,
    );
    setIGRData(igrData);
  }
  const state: RootState = getState();

  if (!igrData) {
    igrData = { played_games: [], recommended_games: [] };
    setIGRData(igrData);
  }

  const playedGameIds = igrData.played_games.map((g: any) => g.game_id);

  let response: AxiosResponse<any> | null = null;
  let gameList: any | null = null;

  response = await Axios.post(
    apiUrl,
    { game_ids: playedGameIds.length ? playedGameIds : ['0'] },
    {
      headers: {
        Authorization: 'Bearer ' + state.authentication.access_token,
        'Content-Type': 'application/json',
      },
    },
  );
  if (response && response.data?.data?.length > 0) {
    gameList = response.data.data;
  }

  let currentGame: string = '';
  let recommendedGame: any | null = null;

  if (gameList?.length > 0) {
    if (window?.location?.pathname?.includes?.('slot-game')) {
      currentGame = window?.location?.pathname?.split?.('/')?.[2];
    }

    const recommendedGameIds = igrData.recommended_games.map((g: any) => g.game_id);
    const eligibleGames = gameList?.filter?.(
      (game: any) =>
        !playedGameIds.includes(game?.id?.toString()) &&
        !recommendedGameIds.includes(game?.id?.toString()) &&
        (!currentGame || game?.id?.toString() !== currentGame),
    );

    recommendedGame = eligibleGames?.[0] || null;
  }

  if (!gameList) {
    return rejectWithValue({ error: 'No data returned from API' });
  }

  if (prefetch) {
    if (recommendedGame) {
      igrData.recommended_games.push({
        game_id: recommendedGame.id.toString(),
        expire_timestamp: expireTimestamp,
      });
      setIGRData(igrData);
      prefetchedGameCache = recommendedGame;
    }
    return { recommendedGame, isOpen: false, isLoading: false };
  }

  let popupGame = recommendedGame;
  if (prefetchedGameCache) {
    popupGame = prefetchedGameCache;
    prefetchedGameCache = null;
  }

  if (popupGame && !igrData.recommended_games.some((g: any) => g.game_id === popupGame.id.toString())) {
    igrData.recommended_games.push({
      game_id: popupGame.id.toString(),
      expire_timestamp: expireTimestamp,
    });
    setIGRData(igrData);
  }

  return { recommendedGame: popupGame, isOpen: true, isLoading: false };
});

export const idleGameRecommendationSlice = createSlice({
  name: 'idleGameRecommendation',
  initialState: <IdleGameRecommendationReducer>{
    isOpen: false,
    recommendedGame: [],
    isLoading: false,
  },
  reducers: {
    closeIGRPopup(state): void {
      state.isOpen = false;
    },
    setIsLoading(state, action: PayloadAction<boolean>): void {
      state.isLoading = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(openIGRPopupAsync.pending, (state): void => {
        state.isLoading = true;
      })
      .addCase(openIGRPopupAsync.fulfilled, (state, action): void => {
        state.isOpen = action.payload.isOpen;
        state.recommendedGame = action.payload.recommendedGame;
        state.isLoading = action.payload.isLoading;
      });
  },
});

export const { closeIGRPopup, setIsLoading } = idleGameRecommendationSlice.actions;

export default idleGameRecommendationSlice.reducer;
